import {
  type CompetitionStandings,
  type StandingsGroup,
  type StandingRow
} from '@h2h/shared';
import { TeamBadge } from '../../../components/TeamBadge';
import { teamCode } from '../../../lib/teams';
import { GoalDiff, Legend } from './GoalDiff';
import { KnockoutBracket } from './KnockoutBracket';
import { isLetteredGroup, PLAYOFF_COLOR, QUALIFY_COLOR } from './constants';

/** Live provider standings — group tables + the knockout bracket (World Cup et al.). */
export function CompetitionStandingsView({
  leagueId,
  standings,
  season
}: {
  leagueId: string;
  standings: CompetitionStandings;
  season?: number;
}) {
  // The provider returns lettered groups (Group A…L) plus, for the World Cup, a
  // "Group Stage" ranking of the third-placed teams. Split them out for clarity.
  const lettered = standings.groups.filter((g) => isLetteredGroup(g.name));
  const groups = lettered.length ? lettered : standings.groups;
  const thirdPlaced = lettered.length
    ? standings.groups.find((g) => !isLetteredGroup(g.name) && g.rows.length > 4)
    : undefined;
  const grouped = groups.length > 1 || !!groups[0]?.name;
  const updated = new Date(standings.updatedAt).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short'
  });

  return (
    <>
      <div className="flex items-center justify-between pt-[18px] pb-3">
        <span className="text-[15px] font-extrabold tracking-[-0.02em] text-ink">
          {grouped ? 'Groups' : 'Standings'}
          {season ? ` · ${season}` : ''}
        </span>
        <span className="text-[12px] font-medium text-ink2">Updated {updated}</span>
      </div>

      {grouped ? (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {groups.map((g) => (
              <GroupCard key={g.name || 'table'} group={g} />
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 px-1">
            <Legend color={QUALIFY_COLOR} label="Top 2 advance" />
            <Legend color={PLAYOFF_COLOR} label="3rd — best-of play-off" />
          </div>
          {thirdPlaced && <ThirdPlacedTable group={thirdPlaced} />}
        </>
      ) : (
        <SingleStandingsTable group={groups[0]} />
      )}

      <KnockoutBracket leagueId={leagueId} />
    </>
  );
}

function GroupCard({ group }: { group: StandingsGroup }) {
  return (
    <div className="overflow-hidden rounded-[18px] border border-black/7 bg-white">
      <div className="flex items-center justify-between bg-fill/55 px-[12px] py-[9px]">
        <span className="text-[12px] font-extrabold tracking-[0.02em] text-ink">{group.name || 'Table'}</span>
        <div className="flex items-center gap-[9px] text-[9px] font-bold text-ink3">
          <span className="w-5 text-center">P</span>
          <span className="w-7 text-center">GD</span>
          <span className="w-6 text-center">PTS</span>
        </div>
      </div>
      {group.rows.map((row) => (
        <GroupRow key={`${group.name}-${row.rank}-${row.teamName}`} row={row} accentTop2 />
      ))}
    </div>
  );
}

function GroupRow({ row, accentTop2 }: { row: StandingRow; accentTop2?: boolean }) {
  const accent = accentTop2
    ? row.rank <= 2
      ? QUALIFY_COLOR
      : row.rank === 3
        ? PLAYOFF_COLOR
        : undefined
    : undefined;
  return (
    <div
      className="flex items-center gap-[9px] border-t border-black/5 px-[12px] py-[8px]"
      style={accent ? { borderLeft: `3px solid ${accent}` } : undefined}
    >
      <span className="num w-[14px] text-center text-[12px] font-extrabold text-ink">{row.rank}</span>
      <TeamBadge
        code={teamCode(row.teamName)}
        logo={row.teamLogo}
        name={row.teamName}
        sizeClass="h-6 w-6"
        roundedClass="rounded-[6px]"
        textClass="text-[8px]"
      />
      <span className="flex-1 truncate text-[12.5px] font-bold tracking-[-0.01em] text-ink">{row.teamName}</span>
      <span className="num w-5 text-center text-[11px] font-semibold text-ink2">{row.played}</span>
      <GoalDiff gd={row.goalDiff} className="w-7 text-[11px]" />
      <span className="num w-6 text-center text-[13px] font-extrabold text-ink">{row.points}</span>
    </div>
  );
}

/** World Cup "best third-placed teams" ranking — the top 8 join the Round of 32. */
function ThirdPlacedTable({ group }: { group: StandingsGroup }) {
  const ADVANCE = 8;
  return (
    <div className="mt-5">
      <div className="flex items-center justify-between pb-2">
        <span className="text-[13px] font-extrabold tracking-[-0.01em] text-ink">Best third-placed teams</span>
        <span className="text-[11px] font-semibold text-ink2">Top {ADVANCE} advance</span>
      </div>
      <div className="overflow-hidden rounded-[18px] border border-black/7 bg-white">
        {group.rows.map((row, i) => (
          <div
            key={`3rd-${row.rank}-${row.teamName}`}
            className="flex items-center gap-[9px] border-t border-black/5 px-[12px] py-[8px] first:border-t-0"
            style={i < ADVANCE ? { borderLeft: `3px solid ${QUALIFY_COLOR}` } : undefined}
          >
            <span className="num w-[14px] text-center text-[12px] font-extrabold text-ink">{i + 1}</span>
            <TeamBadge
              code={teamCode(row.teamName)}
              logo={row.teamLogo}
              name={row.teamName}
              sizeClass="h-6 w-6"
              roundedClass="rounded-[6px]"
              textClass="text-[8px]"
            />
            <span className="flex-1 truncate text-[12.5px] font-bold tracking-[-0.01em] text-ink">{row.teamName}</span>
            <span className="num w-5 text-center text-[11px] font-semibold text-ink2">{row.played}</span>
            <GoalDiff gd={row.goalDiff} className="w-7 text-[11px]" />
            <span className="num w-6 text-center text-[13px] font-extrabold text-ink">{row.points}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** A single-table league standings rendered from live provider data. */
function SingleStandingsTable({ group }: { group?: StandingsGroup }) {
  if (!group) return null;
  return (
    <>
      <div className="px-2">
        <div className="flex items-center gap-[10px] pb-2">
          <span className="w-[18px] text-center text-[10px] font-bold text-ink3">#</span>
          <span className="flex-1 text-[10px] font-bold tracking-[0.04em] text-ink3">TEAM</span>
          <span className="w-7 text-center text-[10px] font-bold text-ink3">P</span>
          <span className="w-[34px] text-center text-[10px] font-bold text-ink3">GD</span>
          <span className="w-[30px] text-center text-[10px] font-bold text-ink3">PTS</span>
        </div>
      </div>
      <div className="overflow-hidden rounded-[20px] border border-black/7 bg-white">
        {group.rows.map((row) => (
          <div
            key={`${row.rank}-${row.teamName}`}
            className="flex items-center gap-[10px] border-b border-black/5 px-[14px] py-[9px] last:border-b-0"
          >
            <span className="num w-[18px] text-center text-[13px] font-extrabold text-ink">{row.rank}</span>
            <TeamBadge
              code={teamCode(row.teamName)}
              logo={row.teamLogo}
              name={row.teamName}
              sizeClass="h-[26px] w-[26px]"
              roundedClass="rounded-[7px]"
              textClass="text-[8px]"
            />
            <span className="flex-1 text-[13px] font-bold tracking-[-0.01em] text-ink">{row.teamName}</span>
            <span className="num w-7 text-center text-[12px] font-semibold text-ink2">{row.played}</span>
            <GoalDiff gd={row.goalDiff} className="w-[34px] text-[12px]" />
            <span className="num w-[30px] text-center text-[14px] font-extrabold text-ink">{row.points}</span>
          </div>
        ))}
      </div>
    </>
  );
}
